import { Box, Button, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconDownloadOutline, IconPrintOutline } from '@zillow/constellation-icons';
import type { CreditReportType } from '@zg-rentals/ts-capps-client';
import { getFullName } from '../../utils/formatters';
import { printHidden } from '../../../styles/panda-styles';
import type { TrackEvent } from '../../models/report-models';
import { CLICKSTREAM_PRINT_CREDIT_REPORT } from '../../constants/clickstream-events';

export function CreditReportHeader({
  creditReport,
  requestedDateTime,
  expirationDateTime,
  onDownloadPdf,
  isDownloadingPdf,
  isInMobileWebView,
  trackEvent,
}: {
  creditReport: CreditReportType;
  requestedDateTime: string;
  expirationDateTime: string;
  onDownloadPdf?: () => void;
  isDownloadingPdf?: boolean;
  isInMobileWebView: boolean;
  trackEvent: TrackEvent;
}) {
  const fullName = getFullName(creditReport);

  return (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <Box>
        <Heading level={2} textStyle="heading-sm">
          {fullName}
        </Heading>
        <Paragraph>Created: {requestedDateTime}</Paragraph>
        <Paragraph>Expiry: {expirationDateTime}</Paragraph>
      </Box>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 'md',
        }}
      >
        {!isInMobileWebView && (
          <Box css={printHidden}>
            {onDownloadPdf ? (
              <Button
                icon={<IconDownloadOutline />}
                onClick={() => {
                  trackEvent(CLICKSTREAM_PRINT_CREDIT_REPORT);
                  onDownloadPdf();
                }}
                loading={isDownloadingPdf}
                size="sm"
              >
                Download
              </Button>
            ) : (
              <TextButton
                icon={<IconPrintOutline />}
                onClick={() => {
                  trackEvent(CLICKSTREAM_PRINT_CREDIT_REPORT);
                  window.print();
                }}
              >
                Print
              </TextButton>
            )}
          </Box>
        )}
        <ExperianLogo />
      </Box>
    </Box>
  );
}

export function ExperianLogo() {
  return (
    <svg width="100px" className="CicLogo" viewBox="0 0 79.846 26.506">
      <g id="layer1" transform="translate(122.914 -181.936)">
        <g id="g135" transform="translate(.5 -.5)">
          <path
            id="path12"
            d="M-110.452 188.942h-2.884a1.547 1.547 0 0 1-1.561-1.56v-2.885c0-.846.688-1.56 1.56-1.56h2.885c.846 0 1.56.687 1.56 1.56v2.884c0 .873-.687 1.561-1.56 1.561"
            fill="#406eb3"
            strokeWidth=".265"
          />
          <path
            id="path14"
            d="M-117.94 195.425h-3.228a1.745 1.745 0 0 1-1.746-1.747v-3.254c0-.953.794-1.746 1.746-1.746h3.255c.979 0 1.746.793 1.746 1.746v3.254c0 .98-.794 1.747-1.773 1.747"
            fill="#632678"
            strokeWidth=".265"
          />
          <path
            id="path16"
            d="M-118.231 202.171h-1.984a1.061 1.061 0 0 1-1.059-1.058v-1.984c0-.582.477-1.059 1.059-1.059h1.984c.582 0 1.058.477 1.058 1.059v1.984c0 .582-.476 1.058-1.058 1.058"
            fill="#982881"
            strokeWidth=".265"
          />
          <path
            id="path18"
            d="M-112.913 208.442h-2.355a1.27 1.27 0 0 1-1.27-1.27v-2.355c0-.688.556-1.27 1.27-1.27h2.355c.688 0 1.27.556 1.27 1.27v2.355c0 .714-.555 1.27-1.27 1.27"
            fill="#ba2f7d"
            strokeWidth=".265"
          />
          <path
            id="path20"
            d="M-104.314 190.133h-1.614a.88.88 0 0 1-.873-.873v-1.614a.88.88 0 0 1 .873-.873h1.614a.88.88 0 0 1 .873.873v1.614c.027.476-.37.873-.873.873"
            fill="#ba2f7d"
            strokeWidth=".265"
          />
          <path
            id="path22"
            d="M-68.701 191.218a.92.92 0 0 0 .926-.926.92.92 0 0 0-.926-.926.943.943 0 0 0-.952.926c0 .502.423.926.952.926z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path24"
            d="M-102.912 201.219c-.132.212-.37.317-.635.317-.132 0-.317-.026-.423-.105-.212-.133-.344-.397-.344-.662 0-.132.027-.264.106-.37l2.17-3.413-2.17-3.414a.661.661 0 0 1-.106-.37.754.754 0 0 1 .767-.767c.265 0 .503.106.662.317l1.773 2.805 1.799-2.805c.132-.211.397-.317.661-.317a.754.754 0 0 1 .767.767.608.608 0 0 1-.105.37l-2.17 3.414 2.17 3.413c.079.106.106.238.106.37 0 .265-.133.556-.344.662a.838.838 0 0 1-.424.105.754.754 0 0 1-.635-.317l-1.799-2.778z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path26"
            d="M-95.16 193.467a4.669 4.669 0 0 1 3.123-1.191c2.62 0 4.683 2.09 4.683 4.683 0 2.567-2.09 4.657-4.683 4.657-1.19 0-2.302-.45-3.122-1.19v3.994c0 .397-.37.741-.768.741-.37 0-.767-.344-.767-.74v-11.325c0-.397.37-.74.767-.74.424 0 .768.343.768.74zm3.096.37a3.094 3.094 0 0 0-3.122 3.122 3.094 3.094 0 0 0 3.122 3.122 3.094 3.094 0 0 0 3.122-3.122c.027-1.746-1.349-3.122-3.122-3.122z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path28"
            d="M-74.098 196.112v4.684c0 .396-.37.74-.768.74-.423 0-.767-.344-.767-.74v-7.7c0-.397.37-.74.767-.74.37 0 .741.317.768.714a3.9 3.9 0 0 1 2.328-.768c.476 0 .847.37.847.768 0 .423-.37.794-.847.794-1.19.026-2.328.45-2.328 2.248z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path30"
            d="M-68.701 192.355c-.423 0-.767.344-.767.741v7.726c0 .397.37.74.767.74.37 0 .767-.343.767-.74v-7.726c0-.397-.37-.74-.767-.74z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path32"
            d="M-58.858 193.096c0-.397.37-.74.767-.74.37 0 .767.343.767.74v7.726c0 .397-.37.74-.767.74-.423 0-.767-.343-.767-.74v-.37a4.72 4.72 0 0 1-3.123 1.19 4.658 4.658 0 0 1-4.683-4.683c0-2.566 2.09-4.657 4.683-4.657 1.191 0 2.276.45 3.123 1.191zm-3.123 6.985a3.094 3.094 0 0 0 3.123-3.122 3.094 3.094 0 0 0-3.123-3.122 3.094 3.094 0 0 0-3.122 3.122 3.094 3.094 0 0 0 3.122 3.122z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path34"
            d="M-49.466 196.218c0-1.296-1.111-2.407-2.407-2.407-1.244 0-2.408.502-2.408 2.328v4.683c0 .397-.37.74-.767.74-.424 0-.768-.343-.768-.74v-7.726c0-.397.37-.74.768-.74.37 0 .74.317.767.714.661-.503 1.482-.768 2.381-.768 2.09 0 3.969 1.64 3.969 3.705v4.815c0 .397-.37.74-.767.74-.424 0-.768-.343-.768-.74z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path36"
            d="M-104.764 196.695c-.026-2.382-2.143-4.393-4.656-4.393a4.652 4.652 0 0 0-4.683 4.657c0 2.62 2.09 4.683 4.683 4.683a4.58 4.58 0 0 0 3.254-1.323.802.802 0 0 0 .265-.582.785.785 0 0 0-.768-.767c-.238 0-.45.132-.608.317-.53.556-1.297.82-2.117.82-1.56 0-2.83-1.058-3.095-2.54h7.064c.026 0 .053 0 .08-.026a.932.932 0 0 0 .581-.846zm-4.656-2.858c1.402 0 2.566.9 2.963 2.17h-5.953c.397-1.27 1.56-2.17 2.99-2.17z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path38"
            d="M-76.93 196.695c-.026-2.382-2.17-4.393-4.656-4.393a4.652 4.652 0 0 0-4.683 4.657c0 2.62 2.09 4.683 4.683 4.683a4.58 4.58 0 0 0 3.254-1.323.802.802 0 0 0 .265-.582.785.785 0 0 0-.767-.767c-.239 0-.45.132-.609.317-.53.556-1.296.82-2.117.82-1.56 0-2.83-1.058-3.095-2.54h7.117c.317-.132.609-.476.609-.872zm-4.656-2.858c1.402 0 2.566.9 2.963 2.17h-5.953c.397-1.27 1.561-2.17 2.99-2.17z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path40"
            d="M-46.29 201.563c-.08 0-.133-.053-.133-.132v-1.297h-.291c-.08 0-.132-.053-.132-.132 0-.08.053-.132.132-.132h.847c.079 0 .132.052.132.132 0 .08-.053.132-.132.132h-.291v1.297c0 .079-.053.132-.133.132z"
            fill="#26478d"
            strokeWidth=".265"
          />
          <path
            id="path42"
            d="M-44.783 201.563c-.079 0-.105-.053-.132-.106l-.37-1.058v1.032c0 .079-.053.132-.133.132-.079 0-.132-.053-.132-.132v-1.376a.18.18 0 0 1 .185-.185c.08 0 .159.052.186.132l.37 1.085.37-1.085a.199.199 0 0 1 .186-.132.18.18 0 0 1 .185.185v1.376c0 .079-.053.132-.133.132-.079 0-.132-.053-.132-.132v-1.032c-.053.158-.132.423-.211.635-.133.423-.16.423-.16.423.027.08-.026.106-.079.106z"
            fill="#26478d"
            strokeWidth=".265"
          />
        </g>
      </g>
    </svg>
  );
}
